'use client';

import React from 'react';
import { RichText } from '@/components/Help/RichText';
import { SupportIllustration } from './SupportIllustration';
import type { ConversationMessage } from './assistant-state';

/** Le texte d'une reponse : des paragraphes, et des listes « 1. » ou « - » ; **gras** comme dans les tutoriels */
function AnswerText({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    const Tag = list.ordered ? 'ol' : 'ul';
    blocks.push(
      <Tag key={blocks.length} className={`mt-1.5 space-y-0.5 pl-[18px] ${list.ordered ? 'list-decimal' : 'list-disc'}`}>
        {list.items.map((item, i) => <li key={i}><RichText text={item} /></li>)}
      </Tag>,
    );
    list = null;
  };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const ordered = line.match(/^\d+[.)]\s+(.*)$/);
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (ordered || bullet) {
      const isOrdered = !!ordered;
      if (list && list.ordered !== isOrdered) flush();
      list ??= { ordered: isOrdered, items: [] };
      list.items.push((ordered ?? bullet)![1]);
      continue;
    }
    flush();
    if (line) blocks.push(<p key={blocks.length} className={blocks.length ? 'mt-1.5' : ''}><RichText text={line} /></p>);
  }
  flush();
  return <>{blocks}</>;
}

export function AssistantMessage({ message }: { message: ConversationMessage }) {
  if (message.role === 'user') {
    return (
      <div className="max-w-[88%] self-end whitespace-pre-wrap break-words rounded-xl rounded-br-[4px] bg-[var(--accent)] px-3 py-2 leading-relaxed text-white">
        {message.text}
      </div>
    );
  }
  return (
    <>
      <span className="-mb-1.5 flex items-center gap-1.5 text-[11px] text-[var(--ink-500)]">
        <SupportIllustration size={16} /> Assistant
      </span>
      <div className="max-w-[88%] self-start break-words rounded-xl rounded-bl-[4px] bg-[var(--ink-100)] px-3 py-2 leading-relaxed text-[var(--ink-800)]">
        {message.state === 'waiting' && !message.text ? (
          <span className="inline-flex gap-1" aria-label="L'assistant réfléchit">
            {[0, 1, 2].map(i => <i key={i} className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--ink-400)]" style={{ animationDelay: `${i * 0.2}s` }} />)}
          </span>
        ) : (
          <AnswerText text={message.text} />
        )}
        {message.state === 'writing' && <span className="ml-0.5 inline-block h-3.5 w-[7px] animate-pulse bg-[var(--ink-600)] align-[-2px]" />}
        {message.state === 'done' && message.sources && message.sources.length > 0 && (
          <div className="mt-2 flex flex-col gap-1 border-t border-[var(--ink-200)] pt-2">
            <small className="text-[11px] text-[var(--ink-500)]">Dans le centre d&apos;aide</small>
            {message.sources.map(s => (
              <a key={s.slug} href={`/aide/${s.slug}`} target="_blank" rel="noopener" className="text-[12.5px] font-semibold text-[var(--accent-700)] hover:underline">
                ↗ {s.title}
              </a>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
